import React, { useEffect, useRef } from 'react';

/**
 * Local confirmation dialog for the Order Booking content writer panel.
 *
 * Deliberately NOT `src/shared/ConfirmationModal.js`: this one layers at
 * `z-[80]` so it sits above the detail drawer (`z-50`), the submit-variations
 * modal (`z-[60]`) and the image-zoom layer (`z-[70]`), and it handles Esc in the
 * CAPTURE phase so the surfaces underneath never see the keystroke.
 *
 * It does not lock body scroll — whatever opened it already did, and a second
 * lock would clobber that restore chain.
 */
const ConfirmDialog = ({
  open,
  title,
  message,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  busy = false,
  onConfirm,
  onCancel,
}) => {
  const titleId = React.useId();
  const panelRef = useRef(null);
  const restoreFocusRef = useRef(null);

  // Refs so the Esc effect can depend on `[open]` alone — no unstable deps.
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const onConfirmRef = useRef(onConfirm);
  onConfirmRef.current = onConfirm;

  // Focus capture on open, restore on close/unmount.
  useEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current = document.activeElement;
    const focusTimer = setTimeout(() => panelRef.current?.focus(), 0);
    return () => {
      clearTimeout(focusTimer);
      const el = restoreFocusRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    };
  }, [open]);

  // Esc, capture phase on `document`: this runs before any bubble-phase
  // listener on `document` (the drawer's and the upload modal's), and
  // stopPropagation() here means they never see the event at all.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      if (!busyRef.current) onCancelRef.current?.();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open]);

  if (!open) return null;

  const confirmTone =
    tone === 'danger' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-sky-600 hover:bg-sky-700';

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancelRef.current?.();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="w-full max-w-sm rounded-2xl bg-white shadow-2xl outline-none"
      >
        <div className="px-5 pb-4 pt-5">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">
            {title}
          </h2>
          {message && <p className="mt-2 text-sm leading-relaxed text-slate-600">{message}</p>}
          {children && <div className="mt-3">{children}</div>}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-200 px-5 py-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => onCancelRef.current?.()}
            disabled={busy}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => onConfirmRef.current?.()}
            disabled={busy}
            className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-60 ${confirmTone}`}
          >
            {busy && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
